import React, { useCallback, useEffect } from 'react';

import { IconButton } from '@eika/button';
import { IconTypes } from '@eika/icon';

import { avtalegiroSelector, useAccounts } from '../../../fetchHooks/useAccount';
import useReceivers from '../../../fetchHooks/useReceivers';
import type { Account } from '../../../generated-types';
import { useFormContext } from '../../../hooks/useForm';
import useTranslation from '../../../hooks/useTranslation';
import useTriggerEffect from '../../../hooks/useTriggerEffect';
import { isValidAccountPaymentFrom, isValidAccountTransferFrom } from '../../../services/accountFilters';
import useToggleSwitchStateChange from '../../NettbankCreateTransactions/components/hooks/useToggleSwitchStateChange';
import useFocusContext from '../../NettbankCreateTransactions/hooks/useFocusContext';
import PaymentForm from '../Payment/PaymentForm';
import NewReceiver from '../Payment/SelectReceiver/NewReceiver';
import { PaymentActionType } from '../Payment/form/actions';
import { validKid } from '../Payment/form/kidService';
import * as paymentFormContext from '../Payment/form/useFormContext';
import TransferForm from '../Transfer/TransferForm';
import { TransferAction, TransferActionType } from '../Transfer/form/actions';
import { TransferState } from '../Transfer/form/state';
import { TransferError } from '../Transfer/form/validate';
import FocusSubModal from './FocusSubModal';
import useMultiformWrapper from './useMultiformWrapper';

import './MultiformWrapper.scss';

type Props = {
    type: 'focus' | 'modal' | 'page';
    transactionType: 'payment' | 'transfer';
    account?: Account;
    onClose?: () => void;
    lockNote?: boolean;
    lockReceiver?: boolean;
    noTabs?: boolean;
} & React.PropsWithChildren;

type TransferFormWrapperProps = { account?: Account } & React.PropsWithChildren;
type PaymentFormWrapperProps = { account?: Account; useExternalShowAddReceiver?: boolean } & React.PropsWithChildren;
const TransferFormWrapper: React.FC<TransferFormWrapperProps> = ({ children, account }) => {
    const { resetForm } = useFocusContext();
    const { handleChange, state } = useFormContext<TransferState, TransferAction, TransferError, TransferActionType>();

    const onReset = useCallback(() => {
        handleChange({
            type: TransferActionType.RESET_WITH_FROM_ACCOUNT,
            payload: isValidAccountTransferFrom(account) ? account?.accountNumber : undefined,
        });
        resetForm.set(false);
    }, [account, handleChange, resetForm]);

    useTriggerEffect(resetForm.value, onReset, [resetForm.value, state.fromAccount]);

    return <>{children}</>;
};

const PaymentFormWrapper: React.FC<PaymentFormWrapperProps> = ({
    children,
    account,
    useExternalShowAddReceiver = false,
}) => {
    const translated = useTranslation();
    const { resetForm, stopTimeoutOnSuccess, active } = useFocusContext();
    const {
        handleChange,
        state,
        showAddNewReciever: [showAddNewReceiver, setShowAddNewReceiver],
        searchString: [searchString, setSearchString],
        redirectLinkAvtalegiro,
    } = paymentFormContext.default();
    const { data: receivers } = useReceivers({ enabled: showAddNewReceiver });
    const { data: avtalegiroAccounts = [] } = useAccounts({ select: avtalegiroSelector });

    useToggleSwitchStateChange(useExternalShowAddReceiver, showAddNewReceiver, redirectLinkAvtalegiro);
    useEffect(() => {
        if (
            state.toAccount?.avtaleGiroSupported &&
            avtalegiroAccounts.length > 0 &&
            validKid(state.toAccount.kidFormats, state.note)
        ) {
            stopTimeoutOnSuccess.set(true);
        } else {
            stopTimeoutOnSuccess.set(false);
        }
        return () => stopTimeoutOnSuccess.set(false);
    }, [state.toAccount, state.note, stopTimeoutOnSuccess, avtalegiroAccounts]);

    const onReset = useCallback(() => {
        handleChange({
            type: PaymentActionType.RESET_WITH_FROM_ACCOUNT,
            payload: isValidAccountPaymentFrom(account) ? account?.accountNumber : undefined,
        });
        setSearchString('');
        resetForm.set(false);
    }, [account, handleChange, resetForm, setSearchString]);

    useTriggerEffect(resetForm.value, onReset, [resetForm.value, state.fromAccount]);

    if (useExternalShowAddReceiver && active.value && showAddNewReceiver) {
        return (
            <FocusSubModal open={showAddNewReceiver} onClose={() => setShowAddNewReceiver(false)}>
                <IconButton
                    className="add-receiver__close-button"
                    icon={IconTypes.KRYSS_24}
                    onClick={() => setShowAddNewReceiver(false)}
                    screenReaderText={translated.closeWindow}
                />
                <div>
                    <NewReceiver
                        receivers={receivers}
                        searchString={searchString}
                        onChange={(selectedReceiver) => {
                            setShowAddNewReceiver(false);
                            setSearchString(selectedReceiver.accountName ?? '');
                            handleChange({
                                type: PaymentActionType.TO_ACCOUNT_SELECTED,
                                payload: selectedReceiver,
                            });
                        }}
                        isNettbank
                        onCancel={() => setShowAddNewReceiver(false)}
                    />
                </div>
            </FocusSubModal>
        );
    }

    return <>{children}</>;
};

const MultiFormWrapper = ({
    type,
    transactionType,
    account,
    onClose = () => null,
    lockNote = false,
    lockReceiver = false,
    noTabs,
}: Props) => {
    const { active } = useFocusContext();

    const { resetFormAndClose, resetForm } = useMultiformWrapper(transactionType, onClose, account);
    return transactionType === 'payment' ? (
        <PaymentFormWrapper account={account} useExternalShowAddReceiver={type === 'focus'}>
            <PaymentForm
                inFocusModal={type === 'focus'}
                isInModal={type === 'modal'}
                useDesktopPlaceholder={type === 'modal'}
                active={active.value}
                account={account}
                onClose={resetFormAndClose}
                onClearPayment={resetForm}
                useTitle={false}
                lockNote={lockNote}
                lockReceiver={lockReceiver}
                noTabs={noTabs}
            />
        </PaymentFormWrapper>
    ) : (
        <TransferFormWrapper account={account}>
            <TransferForm
                isFocusModal={type === 'focus'}
                isInModal={type === 'modal'}
                useDesktopPlaceholder={type === 'modal'}
                useTitle={false}
                active={active.value}
                account={account}
                onClose={resetFormAndClose}
            />
        </TransferFormWrapper>
    );
};
export default MultiFormWrapper;
